// src/components/PaletteSidebar.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Tree, Spin, Typography, Empty, Flex } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { debounce } from 'lodash';
import Fuse from 'fuse.js';
import useTabManagerStore from '../store/tabManagerStore';
import {
  useFlowActions,
  useActivePipelineId,
  useSelectedNodeId,
  useLastNodePosition,
  useActivePipelineNodes,
} from '../store/store';
import { useSteps, useStepsLoading, useStepsError } from '../store/stepStore';
import { DownOutlined, QuestionCircleOutlined } from '@ant-design/icons'; // Fallback icon
import ResizablePanel from './ResizablePanel';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('PaletteSideBar');
import styled from 'styled-components';
// Import centralized edge utilities
import { createAndAddEdge } from '../utils/edgeUtils';
// Import icons
import TableInputIcon from '../assets/icons/tableinput.svg?react';
import TableOutputIcon from '../assets/icons/tableoutput.svg?react';
import FilterRowsIcon from '../assets/icons/filterrows.svg?react';
import { useTranslation } from 'react-i18next';
import { createNodeAndMaybeEdge } from '../utils/nodeCreationUtils';

// Type definitions
interface Step {
  name: string;
  category?: string;
  svg?: string | null;
  type: string;
  nodeType: string;
  stepType: string;
}

interface NodeInfo {
  type: string;
  nodeType: string;
  name: string;
  icon?: string | null;
}

interface TreeNode extends DataNode {
  title: string;
  key: string;
  isCategory?: boolean;
  isLeaf?: boolean;
  svg?: string | null;
  dataType?: string;
  nodeType?: string;
  dataLabel?: string;
  isDraggable?: boolean;
  children?: TreeNode[];
}

interface CategoryData {
  items: Step[];
  children: Record<string, CategoryData>;
}

type CategoryTree = Record<string, CategoryData>;

type IconComponent = React.ComponentType<{ className?: string; style?: React.CSSProperties }>;

// Styled component for icon container that properly sizes SVGs
const IconContainer = styled.span`
  display: inline-flex;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  /* Target any SVG inside this container */
  svg {
    width: 24px !important;
    height: 24px !important;
  }
`;

const { Search } = Input;
const { Title } = Typography;

// Create a mapping from node type string to the icon component
const nodeTypeToIconMap: Record<string, IconComponent> = {
  TableInput: TableInputIcon,
  tableOutput: TableOutputIcon,
  filterRows: FilterRowsIcon,
};

// Fuse.js configuration for fuzzy search in step palette
const fuseOptions = {
  keys: [
    'title', 
    'key',
    // Add normalized versions for better matching
    {
      name: 'normalizedTitle',
      getFn: (item: TreeNode) => item.title?.replace(/[_\-]/g, ' ') || ''
    },
    {
      name: 'compactTitle', 
      getFn: (item: TreeNode) => item.title?.replace(/[_\-\s]/g, '') || ''
    }
  ],
  threshold: 0.4, // Lower = more strict, higher = more fuzzy (0.0 = exact, 1.0 = match anything)
  distance: 100, // Maximum distance to search
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 1,
  ignoreLocation: true, // Don't care where in the string the match is
  findAllMatches: true
};

// Flatten tree structure for Fuse.js search (only leaf nodes)
const flattenTreeData = (nodes: TreeNode[]): TreeNode[] => {
  const flattened: TreeNode[] = [];
  
  const traverse = (nodeList: TreeNode[]) => {
    nodeList.forEach(node => {
      // Only add leaf nodes (actual steps) to search results, not categories
      if (node.isLeaf) {
        flattened.push(node);
      }
      if (node.children) {
        traverse(node.children);
      }
    });
  };
  
  traverse(nodes);
  return flattened;
};

// Rebuild tree structure from search results
const rebuildTreeFromResults = (searchResults: any[], originalTree: TreeNode[]): TreeNode[] => {
  if (searchResults.length === 0) return [];
  
  const resultKeys = new Set(searchResults.map(result => result.item.key));
  const pathsToInclude = new Set<string>();
  
  // Add all parent paths for each result
  searchResults.forEach(result => {
    const path = result.item.key;
    const pathParts = path.split('/');
    for (let i = 1; i <= pathParts.length; i++) {
      pathsToInclude.add(pathParts.slice(0, i).join('/'));
    }
  });
  
  const filterNode = (node: TreeNode): TreeNode | null => {
    const shouldIncludeNode = pathsToInclude.has(node.key) || resultKeys.has(node.key);
    
    if (node.children) {
      const filteredChildren = node.children
        .map(filterNode)
        .filter((child): child is TreeNode => child !== null);
      
      if (filteredChildren.length > 0 || shouldIncludeNode) {
        return { ...node, children: filteredChildren };
      }
    } else if (shouldIncludeNode) {
      return { ...node };
    }
    
    return null;
  };
  
  return originalTree
    .map(filterNode)
    .filter((node): node is TreeNode => node !== null);
};

// Enhanced filter function using Fuse.js for fuzzy matching
const filterAntdTreeAndAncestors = (nodes: TreeNode[], term: string): TreeNode[] => {
  if (!term || term.trim().length === 0) return nodes;
  
  // Flatten the tree for searching
  const flatItems = flattenTreeData(nodes);
  
  // Create Fuse instance
  const fuse = new Fuse(flatItems, fuseOptions);
  
  // Perform search
  const searchResults = fuse.search(term.trim());
  
  // Log search info for debugging
  logger.debug('Fuse.js search results for step palette:', {
    query: term,
    totalItems: flatItems.length,
    results: searchResults.length,
    scores: searchResults.slice(0, 5).map(r => ({ title: r.item.title, score: r.score }))
  });
  
  // Rebuild tree structure with results
  return rebuildTreeFromResults(searchResults, nodes);
};

// --- Helper to get all category keys from a tree structure ---
const getAllCategoryKeys = (nodes: TreeNode[]): string[] => {
  let keys: string[] = [];
  if (!Array.isArray(nodes)) return keys;
  nodes.forEach((node) => {
    if (node?.key && Array.isArray(node.children) && node.children.length > 0) {
      keys.push(node.key);
      keys = keys.concat(getAllCategoryKeys(node.children));
    }
  });
  return keys;
};

// --- Helper: Group transforms by category and build tree ---
const groupByCategory = (transforms: Step[]): TreeNode[] => {
  // Debug: Log the transforms data to see the structure
  logger.debug('Transforms data:', transforms);

  // Create a nested structure for categories
  const categoryTree: CategoryTree = {};

  transforms.forEach((item) => {
    const category = item.category || 'Uncategorized';
    logger.debug('Processing item:', item.name, 'Category:', category);

    // Split category by '/' to handle nested categories
    const categoryParts = category.split('/').map((part) => part.trim());

    // Navigate/create the nested structure
    let currentLevel = categoryTree;
    let currentPath = '';

    categoryParts.forEach((part, index) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (!currentLevel[part]) {
        currentLevel[part] = {
          items: [],
          children: {},
        };
      }

      // If this is the last part, add the item
      if (index === categoryParts.length - 1) {
        currentLevel[part].items.push(item);
      }

      currentLevel = currentLevel[part].children;
    });
  });

  // Convert the nested structure to Ant Design Tree format
  const convertToTreeData = (categoryObj: CategoryTree, parentPath = ''): TreeNode[] => {
    return Object.entries(categoryObj).map(([categoryName, categoryData]) => {
      const fullPath = parentPath ? `${parentPath}/${categoryName}` : categoryName;

      const node: TreeNode = {
        key: fullPath,
        title: categoryName,
        isCategory: true,
        children: [],
      };

      // Add direct items as children
      if (categoryData.items && categoryData.items.length > 0) {
        const itemChildren: TreeNode[] = categoryData.items.map((t) => ({
          key: `${fullPath}/${t.name}`,
          title: t.name,
          isCategory: false,
          isLeaf: true,
          svg: t.svg,
          dataType: t.type,
          nodeType: t.nodeType,
          dataLabel: t.name,
          isDraggable: true,
        }));
        node.children!.push(...itemChildren);
      }

      // Add subcategories as children
      if (categoryData.children && Object.keys(categoryData.children).length > 0) {
        const subcategoryChildren = convertToTreeData(categoryData.children, fullPath);
        node.children!.push(...subcategoryChildren);
      }

      return node;
    });
  };

  const result = convertToTreeData(categoryTree);
  logger.debug('Final tree structure:', result);
  return result;
};

const PaletteSidebar: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [filteredTreeData, setFilteredTreeData] = useState<TreeNode[]>([]);

  // Use transforms store instead of direct fetch
  const transforms = useSteps();
  const loading = useStepsLoading();
  const error = useStepsError();

  // Get tab context
  const { tabs, activeTabId } = useTabManagerStore((state) => ({
    tabs: state.tabs,
    activeTabId: state.activeTabId,
  }));
  const activeTab = tabs.find((tab) => tab.id === activeTabId);
  const activeTabType = activeTab?.type;

  // Filter steps based on tab type
  const filteredSteps = useMemo(() => {
    if (activeTabType === 'pipeline') {
      return transforms.filter((step) => step.stepType === 'transform');
    } else if (activeTabType === 'workflow') {
      return transforms.filter((step) => step.stepType === 'action');
    }
    return [];
  }, [transforms, activeTabType]);

  // Use store.js for both tab state and pipeline operations
  const activePipelineId = useActivePipelineId();
  const { addNode, addEdge, setSelectedNodeId, saveToHistory } = useFlowActions();
  const lastNodePosition = useLastNodePosition();
  const { nodeId: currentSelectedNodeId, isDoubleClicked } = useSelectedNodeId();

  // Get the nodes of the currently active pipeline from the same store as other operations
  const activePipelineNodes = useActivePipelineNodes();

  // Process transforms data when it changes
  const treeData = useMemo(() => {
    if (!filteredSteps || filteredSteps.length === 0) return [];
    return groupByCategory(filteredSteps);
  }, [filteredSteps]);

  // Update filtered data when tree data changes
  useEffect(() => {
    setFilteredTreeData(treeData);
  }, [treeData]);

  // --- Drag and Drop Handler ---
  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLSpanElement>, node: TreeNode): void => {
      if (!node.isDraggable || !node.nodeType || !activePipelineId) {
        event.preventDefault();
        return;
      }
      // Pass all info needed for node creation
      const nodeInfo: NodeInfo = {
        type: node.dataType!, // For icon lookup
        nodeType: node.nodeType, // For node creation
        name: node.dataLabel!,
        icon: node.svg,
      };
      try {
        event.dataTransfer.setData('application/reactflow', JSON.stringify(nodeInfo));
        event.dataTransfer.effectAllowed = 'move';
      } catch (err) {
        logger.error('Error setting drag data:', err);
      }
    },
    [activePipelineId]
  );

  // --- Search Logic (using new filter) ---
  const performSearch = useCallback(
    (term: string): void => {
      if (!term) {
        setFilteredTreeData(treeData);
        setExpandedKeys([]);
        return;
      }
      const filtered = filterAntdTreeAndAncestors(treeData, term);
      setFilteredTreeData(filtered);
      const keysToExpand = getAllCategoryKeys(filtered);
      setExpandedKeys(keysToExpand);
    },
    [treeData]
  );

  const debouncedSearch = useMemo(
    () => debounce((term: string) => performSearch(term), 300),
    [performSearch]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const term = event.target.value;
    setSearchTerm(term);
    debouncedSearch(term);
  };

  const handleTitleClick = useCallback((node: TreeNode): void => {
    if (!node.isLeaf) {
      setExpandedKeys((prev) => {
        const isExpanded = prev.includes(node.key);
        if (isExpanded) {
          return prev.filter((key) => key !== node.key);
        } else {
          return [...prev, node.key];
        }
      });
    }
  }, []);

  const titleRender = useCallback(
    (node: TreeNode): React.ReactNode => {
      if (node.isDraggable) {
        return (
          <span
            draggable
            onDragStart={(e) => handleDragStart(e, node)}
            style={{
              cursor: 'grab',
              userSelect: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {node.svg ? (
              <IconContainer dangerouslySetInnerHTML={{ __html: node.svg }} />
            ) : (
              <QuestionCircleOutlined
                style={{
                  marginRight: 6,
                  color: '#aaa',
                  fontSize: '16px',
                  width: '16px',
                  height: '16px',
                  flexShrink: 0,
                }}
              />
            )}
            {node.title}
          </span>
        );
      }
      // For categories, don't add onClick - let onSelect handle it
      return (
        <span
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {node.title}
        </span>
      );
    },
    [handleDragStart]
  );

  const handleNodeDoubleClick = useCallback(
    (event: React.MouseEvent, antdTreeNode: TreeNode): void => {
      logger.flow.node.debug('Double click on node:', {
        nodeType: antdTreeNode.nodeType,
        dataType: antdTreeNode.dataType,
        nodeLabel: antdTreeNode.dataLabel,
        activePipelineId,
        activePipelineNodes: activePipelineNodes.length,
        currentSelectedNodeId,
        existingNodeIds: activePipelineNodes.map(n => n.id),
      });

      if (!antdTreeNode.isDraggable || !antdTreeNode.nodeType || !activePipelineId) {
        logger.warn('Double click prevented:', {
          isDraggable: antdTreeNode.isDraggable,
          hasNodeType: !!antdTreeNode.nodeType,
          hasActivePipelineId: !!activePipelineId,
        });
        return;
      }

      // Use the shared createNodeAndMaybeEdge function for consistency
      createNodeAndMaybeEdge({
        type: antdTreeNode.dataType!, // For icon lookup
        nodeType: antdTreeNode.nodeType, // For node creation
        name: antdTreeNode.dataLabel!,
        icon: antdTreeNode.svg,
        activePipelineId,
        activePipelineNodes,
        currentSelectedNodeId,
        lastNodePosition,
        addNode,
        addEdge,
        setSelectedNodeId,
        saveToHistory,
        connectEdge: true, // Connect to selected node if one exists
        skipEdgeHistory: true, // Don't include edge creation in undo history for double-click operations
      });
    },
    [
      addNode,
      addEdge,
      setSelectedNodeId,
      saveToHistory,
      lastNodePosition,
      currentSelectedNodeId,
      activePipelineNodes,
      activePipelineId,
    ]
  );

  const handleCategorySelect = useCallback((selectedKeys: React.Key[], info: any): void => {
    // Handle category expansion when clicking on a category
    const { node } = info;
    
    // Debug logging to understand node properties
    logger.debug('Node clicked:', {
      title: node.title,
      key: node.key,
      isCategory: node.isCategory,
      isLeaf: node.isLeaf,
      isDraggable: node.isDraggable,
      hasChildren: node.children && node.children.length > 0
    });
    
    // Check if this is a category (has isCategory: true or has children and is not draggable)
    if (node.isCategory || (node.children && node.children.length > 0 && !node.isDraggable)) {
      logger.debug('Expanding/collapsing category:', node.title);
      setExpandedKeys((prev) => {
        const isExpanded = prev.includes(node.key);
        logger.debug('Current expanded state:', isExpanded);
        if (isExpanded) {
          const newKeys = prev.filter((key) => key !== node.key);
          logger.debug('Collapsing to keys:', newKeys);
          return newKeys;
        } else {
          const newKeys = [...prev, node.key];
          logger.debug('Expanding to keys:', newKeys);
          return newKeys;
        }
      });
    } else {
      logger.debug('Not a category, ignoring click');
    }
  }, []);

  // --- Main Render ---
  return (
    <ResizablePanel>
      <Flex vertical style={{ width: '100%', height: '100%' }}>
        {/* Header with Search */}
        <Flex
          vertical
          gap="small"
          style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}
        >
          <Flex justify="space-between" align="center">
            <Title level={4} style={{ margin: 0 }}>
              {t('palette.title')}
            </Title>
          </Flex>
          <Search
            placeholder={t('palette.search')}
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ width: '100%' }}
            allowClear
          />
        </Flex>

        {/* Tree Area */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px',
          }}
        >
          {loading && (
            <Flex justify="center" align="center" style={{ height: '100%' }}>
              <Spin />
            </Flex>
          )}
          {error && <Empty description={error} style={{ marginTop: '20px' }} />}

          {!loading && !error && filteredTreeData.length > 0 && (
            <Tree
              treeData={filteredTreeData}
              draggable={false} // Dragging is handled by titleRender's span
              selectable={true} // Enable selection to handle clicks
              selectedKeys={[]} // Keep no persistent selection
              blockNode
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              onSelect={handleCategorySelect}
              titleRender={titleRender}
              showLine={{ showLeafIcon: false }}
              virtual={false} // Keep virtualization off
              onDoubleClick={(event, node) => handleNodeDoubleClick(event, node)}
              style={{ overflowX: 'hidden' }}
              switcherIcon={<DownOutlined />}
            />
          )}
          {!loading && !error && filteredTreeData.length === 0 && (
            <Empty
              description={searchTerm ? t('palette.noSteps') : t('palette.noSteps')}
              style={{ marginTop: '20px' }}
            />
          )}
        </div>
      </Flex>
    </ResizablePanel>
  );
};

export default PaletteSidebar;
